import React, { useContext, useMemo, useState } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { MainContainer, ContentContainer } from '../../elements';
import { SALE_UPDATE_ASSETS, LOT_NUMBER_SUGGESTION } from './queries';
import { Text, View } from 'react-native';

import DataState from 'app/src/components/DataState';
import { SALES_WITH_COUNT } from 'app/src/components/Sales/queries';
import { Button } from 'app/src/elements/buttons';
import AuthContext from 'app/src/contexts/AuthContext';
import { LOT_NUMBER_YEARS } from 'app/src/components/Sales/Process';
import { Warning, WarningText } from 'app/src/styles';

const inputErrorStyle = {
  border: '1px solid #c62828',
  outline: 'none',
};

const errorTextStyle = {
  color: '#c62828',
  fontSize: '12px',
  fontWeight: 600,
  marginTop: '6px',
};

const AddAssetToSale = ({ assetId, setShowModal, saleAsset, refetch }) => {
  const { lotPrefixes } = useContext(AuthContext);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const [values, setValues] = useState({
    prefix: '',
    year: '',
    suffix: '',
    saleId: '',
    available: [],
    assetId,
  });

  const [updateAssets] = useMutation(SALE_UPDATE_ASSETS, {
    onCompleted: data => {
      const errors = data?.saleUpdateAssets?.errors || [];

      if (errors.length > 0) {
        const message = errors.map(error => error.message).join(', ');
        setSubmitting(false);
        setFormError('Unable to add asset. Please fix the run number below.');
        setFieldErrors({
          suffix: message || 'Run order is invalid.'
        });
        return;
      }

      setShowModal(false);
      refetch();
    },
    onError: error => {
      setSubmitting(false);
      setFormError('Unable to add asset. Please fix the highlighted field.');
      setFieldErrors({
        suffix: error.message
      });
    }
  });

  const { loading, error, data } = useQuery(SALES_WITH_COUNT);

  const [loadLotNumberSuggestion] = useLazyQuery(
    LOT_NUMBER_SUGGESTION,
    {
      onCompleted: suggestionData => {
        setValues(currentValues => ({
          ...currentValues,
          prefix: suggestionData.lotNumberSuggestion.lotNumberPrefix,
          year: String(
            suggestionData.lotNumberSuggestion.lotNumberYear || ''
          ).padStart(2, '0'),
          suffix: suggestionData.lotNumberSuggestion.lotNumberSuffix,
          available: suggestionData.lotNumberSuggestion.availableSuffixes || [],
        }));
      }
    }
  );

  const sales = useMemo(() => {
    return (data?.salesWithCount?.entries || []).filter(
      sale => !(['Processing', 'Closed'].includes(sale.status))
    );
  }, [data]);

  const selectedSale = useMemo(() => {
    if (!sales.length) { return null; }
    return sales.find(sale => String(sale.id) === String(values.saleId)) || sales[0];
  }, [sales, values.saleId]);

  const publicAuction = !!selectedSale?.publicAuction;

  if (loading) {
    return <DataState.Loading />;
  }

  if (error) {
    return <DataState.Error error={error} />;
  }

  if (sales.length === 0) {
    return <Text>No upcoming sales</Text>;
  }

  const clearFieldError = field => {
    if (!fieldErrors[field]) { return; }
    setFieldErrors(current => ({ ...current, [field]: null }));
  };

  const updateValues = ({ saleId, prefix, year, suffix }) => {
    const newValues = { ...values };

    if (typeof saleId !== 'undefined') {
      newValues.saleId = saleId;

      const nextSale = sales.find(sale => String(sale.id) === String(saleId)) || sales[0];

      if (nextSale?.publicAuction) {
        newValues.year = '';
        newValues.available = [];
      } else {
        newValues.suffix = '';
        newValues.available = [];
      }

      setFormError('');
      setFieldErrors({});
    }

    if (typeof prefix !== 'undefined') {
      newValues.prefix = prefix;

      if (publicAuction) {
        newValues.year = '';
      } else {
        newValues.suffix = '';
      }

      clearFieldError('prefix');
      setFormError('');
    }

    if (typeof year !== 'undefined') {
      newValues.year = year;
      newValues.suffix = '';
      clearFieldError('year');
      setFormError('');
    }

    if (typeof suffix !== 'undefined') {
      newValues.suffix = suffix;
      clearFieldError('suffix');
      setFormError('');
    }

    const currentSale =
      sales.find(sale => String(sale.id) === String(newValues.saleId)) || sales[0];

    if (
      !currentSale?.publicAuction &&
      newValues.prefix &&
      (typeof saleId !== 'undefined' || typeof prefix !== 'undefined' || typeof year !== 'undefined')
    ) {
      loadLotNumberSuggestion({
        variables: {
          saleId: currentSale.id,
          assetId: newValues.assetId,
          prefix: newValues.prefix,
          year: (newValues.year ? parseInt(newValues.year, 10) : null),
        }
      });
    }

    setValues(newValues);
  };

  const onSubmit = () => {
    if (submitting) { return; }

    const currentSale = selectedSale || sales[0];
    const nextFieldErrors = {};

    if (!values.prefix) {
      nextFieldErrors.prefix = 'Please select a lot number prefix.';
    }

    if (currentSale?.publicAuction) {
      const runNumber = parseInt(values.suffix, 10);

      if (!values.suffix) {
        nextFieldErrors.suffix = 'Please enter a run number.';
      } else if (!runNumber || runNumber <= 0 || String(runNumber) !== String(values.suffix).trim()) {
        nextFieldErrors.suffix = 'Run number must be a whole positive integer.';
      }
    } else {
      if (!values.year) {
        nextFieldErrors.year = 'Please select a lot year.';
      }

      if (!values.suffix) {
        nextFieldErrors.suffix = 'Please select a lot suffix.';
      }
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setFormError('Unable to add asset. Please fix the highlighted field.');
      return;
    }

    setSubmitting(true);
    setFormError('');
    setFieldErrors({});

    updateAssets({
      variables: {
        saleId: currentSale.id,
        moveFromOtherSales: !!saleAsset,
        assetInputs: [{
          assetId,
          lotNumberPrefix: values.prefix,
          lotNumberYear: currentSale?.publicAuction
            ? null
            : (values.year ? parseInt(values.year, 10) : null),
          lotNumberSuffix: values.suffix ? parseInt(values.suffix, 10) : null,
          manualRunOrder: currentSale?.publicAuction && values.suffix
            ? parseInt(values.suffix, 10)
            : null,
        }]
      }
    });
  };

  return (
    <MainContainer>
      <ContentContainer>
        {saleAsset && (
          <Warning>
            <WarningText>
              Asset is currently listed in {saleAsset.sale.name}.
            </WarningText>
            <WarningText>
              Adding it to a new sale will remove it from {saleAsset.sale.name}.
            </WarningText>
          </Warning>
        )}

        {formError ? (
          <Warning>
            <WarningText>{formError}</WarningText>
          </Warning>
        ) : null}

        <Text>Sales:</Text>
        <select
          type="select"
          value={values.saleId}
          onChange={e => updateValues({ saleId: e.target.value })}
        >
          {sales.map(sale => (
            <option key={`sale-${sale.id}`} value={sale.id}>
              {sale.name}
            </option>
          ))}
        </select>

        <Text>Lot Number:</Text>

        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <div>
            <select
              type="select"
              value={values.prefix}
              onChange={e => updateValues({ prefix: e.target.value })}
              style={fieldErrors.prefix ? inputErrorStyle : {}}
            >
              <option value=""></option>
              {lotPrefixes?.map(prefix => (
                <option key={prefix.code} value={prefix.code}>
                  {prefix.code}
                </option>
              ))}
            </select>
            {fieldErrors.prefix ? (
              <div style={errorTextStyle}>{fieldErrors.prefix}</div>
            ) : null}
          </div>

          <Text>-</Text>

          {publicAuction ? (
            <div>
              <input
                type="number"
                min="1"
                step="1"
                value={values.suffix || ''}
                onChange={e => updateValues({ suffix: e.target.value })}
                style={{
                  width: '90px',
                  ...(fieldErrors.suffix ? inputErrorStyle : {})
                }}
              />
              {fieldErrors.suffix ? (
                <div style={errorTextStyle}>{fieldErrors.suffix}</div>
              ) : null}
            </div>
          ) : (
            <>
              <div>
                <select
                  type="select"
                  value={values.year}
                  onChange={e => updateValues({ year: e.target.value })}
                  style={fieldErrors.year ? inputErrorStyle : {}}
                >
                  <option value=""></option>
                  {LOT_NUMBER_YEARS?.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                {fieldErrors.year ? (
                  <div style={errorTextStyle}>{fieldErrors.year}</div>
                ) : null}
              </div>

              <div>
                <select
                  type="select"
                  value={values.suffix}
                  onChange={e => updateValues({ suffix: e.target.value })}
                  style={fieldErrors.suffix ? inputErrorStyle : {}}
                >
                  <option value=""></option>
                  {(values.available || []).map(suffix => (
                    <option key={suffix} value={suffix}>{suffix}</option>
                  ))}
                </select>
                {fieldErrors.suffix ? (
                  <div style={errorTextStyle}>{fieldErrors.suffix}</div>
                ) : null}
              </div>
            </>
          )}
        </View>

        {!submitting && (
          <Button onPress={() => onSubmit()} label={'Add to Sale'} />
        )}
      </ContentContainer>
    </MainContainer>
  );
};

export default AddAssetToSale;